// Snippets typed one after another, in a loop, on the code screen of the background.
// No size limit: the screen scrolls. Avoid `#` outside Python and PHP strings, the
// highlighter reads it as a comment.

const attention = `
// core/cortex/attention.ts: multi-head scaled dot-product attention
import { Tensor, type AttentionWeights } from './tensor';

export class MultiHeadAttention {
    private readonly scale: number;

    constructor(
        private readonly heads: number,
        private readonly dim: number,
        private readonly weights: AttentionWeights,
    ) {
        this.scale = 1 / Math.sqrt(dim / heads);
    }

    forward(x: Tensor, mask?: Tensor): Tensor {
        const [batch, tokens] = x.shape;
        const split = (t: Tensor) => t.reshape([batch, tokens, this.heads, -1]).transpose([0, 2, 1, 3]);

        const q = split(x.matmul(this.weights.query));
        const k = split(x.matmul(this.weights.key));
        const v = split(x.matmul(this.weights.value));

        let scores = q.matmul(k.transpose([0, 1, 3, 2])).mul(this.scale);
        if (mask) scores = scores.where(mask, -Infinity);

        const context = softmax(scores, -1).matmul(v);
        return context.transpose([0, 2, 1, 3]).reshape([batch, tokens, this.dim]).matmul(this.weights.output);
    }
}

/** Numerically stable softmax along \`axis\`. */
export function softmax(x: Tensor, axis: number): Tensor {
    const exp = x.sub(x.max(axis, true)).exp();
    return exp.div(exp.sum(axis, true));
}`;

const orbit = `
# nav/orbit.py: two-body propagator with J2 oblateness, RK4 integration
import numpy as np

MU = 3.986004418e14  # Earth's gravitational parameter, m^3/s^2
R_EARTH = 6378137.0
J2 = 1.08262668e-3


def acceleration(r: np.ndarray) -> np.ndarray:
    """Point-mass gravity plus the J2 term of the planet's bulge."""
    x, y, z = r
    norm = np.linalg.norm(r)
    k = 1.5 * J2 * MU * R_EARTH**2 / norm**5
    zz = 5 * z**2 / norm**2
    return -MU * r / norm**3 + k * np.array([x * (zz - 1), y * (zz - 1), z * (zz - 3)])


def rk4_step(state: np.ndarray, dt: float) -> np.ndarray:
    def derivative(s):
        return np.concatenate((s[3:], acceleration(s[:3])))

    k1 = derivative(state)
    k2 = derivative(state + dt / 2 * k1)
    k3 = derivative(state + dt / 2 * k2)
    k4 = derivative(state + dt * k3)
    return state + dt / 6 * (k1 + 2 * k2 + 2 * k3 + k4)


def propagate(state: np.ndarray, duration: float, dt: float = 10.0):
    """Yields (t, position, velocity) every step until \`duration\` seconds."""
    t = 0.0
    while t < duration:
        state = rk4_step(state, dt)
        t += dt
        yield t, state[:3], state[3:]`;

const hashRing = `
// cluster/HashRing.php: consistent hashing over virtual nodes
final class HashRing
{
    /** @var array<int, string> position on the ring => node id */
    private array $ring = [];
    private array $positions = [];

    public function __construct(private readonly int $replicas = 160) {}

    public function add(string $node): void
    {
        for ($i = 0; $i < $this->replicas; $i++) {
            $this->ring[$this->hash("{$node}#{$i}")] = $node;
        }
        ksort($this->ring);
        $this->positions = array_keys($this->ring);
    }

    public function remove(string $node): void
    {
        $this->ring = array_filter($this->ring, fn (string $owner) => $owner !== $node);
        $this->positions = array_keys($this->ring);
    }

    /** First node clockwise from the key's position, wrapping around. */
    public function locate(string $key): string
    {
        $hash = $this->hash($key);
        [$low, $high] = [0, count($this->positions)];
        while ($low < $high) {
            $mid = intdiv($low + $high, 2);
            if ($this->positions[$mid] < $hash) $low = $mid + 1;
            else $high = $mid;
        }
        return $this->ring[$this->positions[$low] ?? $this->positions[0]];
    }

    private function hash(string $value): int
    {
        return unpack('J', hash('xxh64', $value, true))[1] & PHP_INT_MAX;
    }
}`;

const raymarch = `
// render/raymarch.js: sphere tracing a signed distance field
const MAX_STEPS = 128;
const EPSILON = 1e-4;
const FAR_PLANE = 100;

const length = ([x, y, z]) => Math.hypot(x, y, z);
const add = (a, b) => a.map((v, i) => v + b[i]);
const sub = (a, b) => a.map((v, i) => v - b[i]);
const scale = (a, s) => a.map((v) => v * s);
const normalize = (a) => scale(a, 1 / length(a));

/** Smooth union of two distances, blended over radius k. */
function smin(a, b, k) {
    const h = Math.max(k - Math.abs(a - b), 0) / k;
    return Math.min(a, b) - h * h * k * 0.25;
}

function scene(p, time) {
    const core = length(p) - 1.2;
    const orbit = [Math.cos(time) * 2, Math.sin(time * 1.3), Math.sin(time) * 2];
    const moon = length(sub(p, orbit)) - 0.4;
    const ring = Math.hypot(Math.hypot(p[0], p[2]) - 2.6, p[1]) - 0.05;
    return Math.min(smin(core, moon, 0.8), ring);
}

function normalAt(p, time) {
    const d = scene(p, time);
    return normalize([
        scene(add(p, [EPSILON, 0, 0]), time) - d,
        scene(add(p, [0, EPSILON, 0]), time) - d,
        scene(add(p, [0, 0, EPSILON]), time) - d,
    ]);
}

export function march(origin, direction, time) {
    let travelled = 0;
    for (let step = 0; step < MAX_STEPS; step++) {
        const p = add(origin, scale(direction, travelled));
        const distance = scene(p, time);
        if (distance < EPSILON) return { hit: true, point: p, normal: normalAt(p, time), step };
        travelled += distance;
        if (travelled > FAR_PLANE) break;
    }
    return { hit: false, step: MAX_STEPS };
}`;

const tracker = `
# sensors/tracker.py: constant-velocity Kalman filter for contact tracking
import numpy as np


class ContactTracker:
    """Fuses noisy radar fixes into a smoothed position and velocity."""

    def __init__(self, dt: float, process_noise=1e-2, sensor_noise=4.0):
        self.x = np.zeros(6)  # px, py, pz, vx, vy, vz
        self.P = np.eye(6) * 500.0
        self.F = np.eye(6)
        self.F[:3, 3:] = np.eye(3) * dt
        self.H = np.hstack((np.eye(3), np.zeros((3, 3))))
        self.Q = np.eye(6) * process_noise
        self.R = np.eye(3) * sensor_noise

    def predict(self) -> np.ndarray:
        self.x = self.F @ self.x
        self.P = self.F @ self.P @ self.F.T + self.Q
        return self.x[:3]

    def update(self, fix: np.ndarray) -> None:
        innovation = fix - self.H @ self.x
        S = self.H @ self.P @ self.H.T + self.R
        K = self.P @ self.H.T @ np.linalg.inv(S)
        self.x = self.x + K @ innovation
        self.P = (np.eye(6) - K @ self.H) @ self.P

    def gate(self, fix: np.ndarray, threshold=11.34) -> bool:
        """Mahalanobis gate: rejects fixes unlikely to belong to this contact."""
        innovation = fix - self.H @ self.x
        S = self.H @ self.P @ self.H.T + self.R
        return innovation @ np.linalg.solve(S, innovation) < threshold`;

const hyperlane = `
// nav/hyperlane.ts: A* route through the jump network
import { PriorityQueue, distance, type Vec3 } from './math';

interface Star {
    id: string;
    position: Vec3;
    lanes: { to: string; fuel: number }[]; // fuel >= length: the heuristic never overshoots
}

export function plotRoute(chart: Map<string, Star>, from: string, to: string): string[] | null {
    const goal = chart.get(to)!.position;
    const heuristic = (id: string) => distance(chart.get(id)!.position, goal);
    const cost = new Map([[from, 0]]);
    const cameFrom = new Map<string, string>();
    const open = new PriorityQueue<string>();
    open.push(from, heuristic(from));

    while (open.size) {
        const current = open.pop()!;
        if (current === to) return unwind(cameFrom, current);

        for (const { to: next, fuel } of chart.get(current)!.lanes) {
            const tentative = cost.get(current)! + fuel;
            if (tentative >= (cost.get(next) ?? Infinity)) continue;
            cameFrom.set(next, current);
            cost.set(next, tentative);
            open.push(next, tentative + heuristic(next));
        }
    }
    return null; // no lane reaches the target
}

function unwind(cameFrom: Map<string, string>, node: string): string[] {
    const path = [node];
    while (cameFrom.has(node)) path.unshift((node = cameFrom.get(node)!));
    return path;
}`;

export const backgroundCode = [attention, orbit, hashRing, raymarch, tracker, hyperlane].map((code) => code.trimStart()); // drop the literals' leading newline
